import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import loadable from '@loadable/component'
import Progress from '@edulastic/common/src/components/Progress'
import {
  EduButton,
  FlexContainer,
  Label,
  SelectInputStyled,
  TextInputStyled,
} from '@edulastic/common'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { compose } from 'redux'
import { LANGUAGE_EN } from '@edulastic/constants/const/languages'
import { languageByCode } from '@edulastic/constants/const/test'
import { Tooltip } from 'antd'
import LanguageSelectorTab from '../../../../../../../common/components/LanguageSelectorTab'
import useTestLanguageTab from '../../../../../../../common/components/LanguageSelectorTab/hooks/useTestLanguageTab'
import { changeTestDataToPreferredLanguage } from '../../../../../../../assessment/utils/test'

import CustomizeTTSModal from '../../../../../../src/components/common/CustomizeTTSModal/CustomizeTTSModal'
import useTestCustomizeTTS from '../../../../../../src/components/common/CustomizeTTSModal/hooks/useTestCustomizeTTS'

import {
  fetchTestTTSTextAction,
  setTTSTextStateAction,
  updateTestTTSTextAction,
} from '../../../../../../src/actions/ttsText'
import {
  getTTSTextAPIStatusSelector,
  getTTSTextResultSelector,
  updateTTSTextAPIStatusSelector,
} from '../../../../../../src/selectors/ttsText'
import { Description } from '../styled'
import {
  getLanguageTabsSelector,
  languageStateSelector,
  setLangAction,
  setLangTabsAction,
} from '../../../../../../../common/components/LanguageSelectorTab/duck'
import { getTestsLoadingSelector } from '../../../../../ducks'
import { getAvailableLanguageKeysInTest } from '../../../../../../../common/utils/helpers'
import { filterAndOrderByLanguageOptions } from '../../../../../../questionUtils'

const FroalaEditor = loadable(() =>
  import(
    /* webpackChunkName: "froalaCommonChunk" */ '@edulastic/common/src/components/FroalaEditor'
  )
)
const STUDENT_ACK_OPTIONS = [
  { label: "Don't show to students", value: false },
  { label: 'Show to students', value: true },
]

const Instruction = ({
  updateTestData,
  disabled,
  showInstructionAcknowledgement,
  currentSettingsId,
  test = {},
  ttsTextAPIStatus,
  ttsTextResult,
  updateTTSAPIStatus,
  setTTSTextState,
  fetchTTSText,
  updateTTSText,
  setLanguage,
  setLanguageTabs,
  currentLang,
  languageTabsToShow,
  isLoading,
  isDocBased,
  isMultiLanguageEnabled,
}) => {
  const {
    popoverVisible,
    selectedLanguages,
    setPopoverVisible,
    handleSaveLanguage,
    handleSelectedLang,
    handleChangeLanguage,
  } = useTestLanguageTab({
    test,
    currentSettingsId,
    setLanguage,
    setLanguageTabs,
    currentLang,
    languageTabsToShow,
    isDocBased,
  })

  const { hasInstruction } = test

  const firstTabLanguage = filterAndOrderByLanguageOptions(
    getAvailableLanguageKeysInTest(test)
  )?.[0]

  const {
    showTTSTextModal,
    setShowTTSTextModal,
    modalDraggable,
    setModalDraggable,
    voiceLanguage,
    setVoiceLanguage,
    requestToGetTTSText,
    setSelectedLanguage,
    selectedLanguage,
  } = useTestCustomizeTTS({
    ttsTextAPIStatus,
    ttsTextResult,
    updateTTSAPIStatus,
    setTTSTextState,
    fetchTTSText,
    updateTTSText,
    testData: test,
    firstTabLanguage,
  })

  const testByLanguage = changeTestDataToPreferredLanguage(test, currentLang)
  const { instruction, instructionAcknowledgementText } = testByLanguage

  const onChange = (value, inputName) => {
    if (disabled) return
    if (inputName === 'showInstructionAcknowledgement') {
      updateTestData(inputName)(value)
    } else {
      updateTestData(inputName, currentLang)(value)
    }
  }

  const onClickCustomizeTTS = () => {
    setShowTTSTextModal(true)
  }

  const toggleTTSTextModal = () => {
    setShowTTSTextModal(!showTTSTextModal)
  }

  const [defaultLanguage = LANGUAGE_EN] = selectedLanguages

  return (
    <>
      {isMultiLanguageEnabled && hasInstruction ? (
        <Description id="test-instructions-description">
          Students receive Test Instructions in their preferred language, or in{' '}
          {languageByCode[defaultLanguage]} if no preference is set. To require
          students to Acknowledge before starting the assignment choose
          &ldquo;Show to Students&rdquo;
        </Description>
      ) : null}

      {isMultiLanguageEnabled && (
        <LanguageSelectorTab
          isTestSettings
          isDisabled={disabled}
          popoverVisible={popoverVisible}
          setPopoverVisible={setPopoverVisible}
          selectedLanguages={selectedLanguages}
          handleSaveLanguage={handleSaveLanguage}
          handleSelectedLang={handleSelectedLang}
          handleChangeLanguage={handleChangeLanguage}
          currentLang={currentLang}
          languageTabsToShow={languageTabsToShow}
        />
      )}
      <FlexContainer
        flexDirection="column"
        gap="16px"
        align="flex-start"
        padding="16px 0 0 0"
      >
        <div>
          <StyledLabel
            fontSize="12px"
            fontWeight="600"
            textTransform="uppercase"
            marginBottom="8px"
            id="test-instructions-label"
          >
            TEST INSTRUCTIONS
          </StyledLabel>

          <StyledEditor
            fallback={<Progress />}
            toolbarId="test-instruction"
            value={instruction}
            onChange={(value) => onChange(value, 'instruction')}
            placeholder="Answers will be checked for plagiarism"
            readOnly={disabled}
            border="border"
            toolbarSize="SM"
            key={`instruction-editor-${currentLang}-${disabled}`}
          />
        </div>
        {isMultiLanguageEnabled && (
          <div>
            <StyledLabel
              fontSize="12px"
              fontWeight="600"
              textTransform="uppercase"
              marginBottom="8px"
              id="test-instructions-acknowledgement"
            >
              STUDENT ACKNOWLEDGEMENT
            </StyledLabel>
            <CombinedInputContainer>
              <SelectInputStyled
                value={showInstructionAcknowledgement}
                onChange={(value) =>
                  onChange(value, 'showInstructionAcknowledgement')
                }
                disabled={disabled}
                width="220px"
                height="40px"
                key={`instruction-acknowledgement-visibility-${currentLang}-${disabled}`}
                data-cy="acknowledgementVisibilityDropdown"
              >
                {STUDENT_ACK_OPTIONS.map((option) => (
                  <SelectInputStyled.Option
                    key={`test_instruction_acknowledgement_visibility_${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </SelectInputStyled.Option>
                ))}
              </SelectInputStyled>

              <TextInputStyled
                placeholder="I have read and understood the instructions."
                disabled={disabled}
                height="42px"
                margin="0 0 0 -1px"
                onChange={(e) => {
                  e.preventDefault()
                  onChange(e.target?.value, 'instructionAcknowledgementText')
                }}
                value={instructionAcknowledgementText}
                key={`instruction-acknowledgement-text-${currentLang}-${disabled}`}
                data-cy="instructionAcknowledgementText"
              />
            </CombinedInputContainer>
          </div>
        )}
        {isMultiLanguageEnabled && (
          <Tooltip
            title={!test?._id ? 'Please save the test to customize TTS' : ''}
          >
            <span style={{ width: '120px' }}>
              <EduButton
                isGhost
                height="38px"
                width="120px"
                data-cy="viewTTSText"
                onClick={onClickCustomizeTTS}
                title="Customize TTS"
                ml="0px"
                disabled={disabled || test?._id === undefined}
                key={`customize-tts-${currentLang}-${disabled}`}
              >
                Customize TTS
              </EduButton>
            </span>
          </Tooltip>
        )}
      </FlexContainer>
      {showTTSTextModal && (
        <CustomizeTTSModal
          showTTSTextModal={showTTSTextModal}
          setShowTTSTextModal={setShowTTSTextModal}
          modalDraggable={modalDraggable}
          setModalDraggable={setModalDraggable}
          voiceLanguage={voiceLanguage}
          setVoiceLanguage={setVoiceLanguage}
          testData={test}
          toggleTTSTextModal={toggleTTSTextModal}
          ttsTextAPIStatus={ttsTextAPIStatus}
          updateTTSAPIStatus={updateTTSAPIStatus}
          ttsTextResult={ttsTextResult}
          updateTTSText={updateTTSText}
          regenerateTTSText={() => requestToGetTTSText(true)}
          onLanguageChange={(value) => setSelectedLanguage(value)}
          onChangeVoiceLanguge={() => {}}
          selectedLanguage={selectedLanguage}
          handleDraggable={setModalDraggable}
          isLoading={isLoading}
          showInstructionAcknowledgement={showInstructionAcknowledgement}
          sourceType="test"
        />
      )}
    </>
  )
}

Instruction.propTypes = {
  updateTestData: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

Instruction.defaultProps = {
  disabled: false,
}

const enhance = compose(
  withRouter,
  connect(
    (state) => {
      return {
        ttsTextAPIStatus: getTTSTextAPIStatusSelector(state),
        ttsTextResult: getTTSTextResultSelector(state),
        updateTTSAPIStatus: updateTTSTextAPIStatusSelector(state),
        currentLang: languageStateSelector(state),
        languageTabsToShow: getLanguageTabsSelector(state),
        isLoading: getTestsLoadingSelector(state),
      }
    },
    {
      setTTSTextState: setTTSTextStateAction,
      fetchTTSText: fetchTestTTSTextAction,
      updateTTSText: updateTestTTSTextAction,
      setLanguage: setLangAction,
      setLanguageTabs: setLangTabsAction,
    }
  )
)

export default enhance(Instruction)

const StyledEditor = styled(FroalaEditor)`
  .fr-box {
    border-radius: 2px;
    min-height: 40px;
  }
`

const CombinedInputContainer = styled(FlexContainer)`
  .ant-select-selection {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    color: #444 !important;
    font-weight: normal !important;
    font-size: 14px !important;
  }

  .ant-input {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    font-size: 14px !important;
    font-weight: normal !important;
    color: #444 !important;
  }
`
const StyledLabel = styled(Label)`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 8px;
`
